import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { select } from '@ngxs/store';
import { TablerIconComponent } from 'angular-tabler-icons';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { TabComponent } from 'src/app/ui/tabs/tab/tab.component';
import { TabsComponent } from 'src/app/ui/tabs/tabs.component';
import { IntakeResponsesTableComponent } from './components/intake-responses-table/intake-responses-table.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import {
	FieldResponseStatus,
	FormAnswerRequest,
	IntakeFormStatus,
	SubmitIntakeFormResponsesRequest,
	SubmitIntakeFormResponsesResponse,
} from '@models/client-intake';
import { environment } from 'src/environments/environment';
import { IntakeFormClientResponsesSelectors } from '@domain/intake-form-client-responses';
import { UserProfileState } from '@state/user-profile';
import { $reviewResponsesViewModelSelector } from './view-models/selectors';
import {
	type ReviewResponseItem,
	type IntakeResponseSection,
	type IntakeResponseRow,
} from './view-models/models';
import {
	toggleItemSelection,
	selectAllForForm,
	applyCheckboxStates,
} from './utils/response-helpers';
import { Logger } from 'src/app/shared/core/logger';
import { IntakeStatusDisplayComponent } from '../client-intake-page/components/intake-status-display/intake-status-display.component';
import { FileUploadMetaData } from '@core/ui';
import { UiModule } from 'src/app/ui/ui.module';
import { ClientIntakeService } from '@services/client-intake/client-intake.service';
import { KebabOption } from '../components/client-intake-actions/view-models/models';
import { MatterFileType } from '@models/matter';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';

@Component({
	selector: 'app-client-intake-review-responses',
	templateUrl: './client-intake-review-responses-page.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		ButtonComponent,
		TabsComponent,
		TabComponent,
		TablerIconComponent,
		IntakeResponsesTableComponent,
		IntakeStatusDisplayComponent,
		UiModule,
	],
})
export class ClientIntakeReviewResponsesPageComponent {
	matterId = select(SelectedMatter.id);
	businessId = select(UserProfileState.getCurrentBusinessId);
	currentClientIntakeResponse = select(
		IntakeFormClientResponsesSelectors.$intakeFormResponses,
	);

	viewModelData_ = select($reviewResponsesViewModelSelector);

	selectedFormId_ = signal<string | null>(null);
	checkboxStates_ = signal<Record<string, Record<string, boolean>>>({});
	fileType = select(SelectedMatter.matterFileType);

	navigationService = inject(NavigationService);
	route = inject(ActivatedRoute);
	httpClient = inject(HttpClient);
	clientIntakeService = inject(ClientIntakeService);
	analyticsService = inject(AnalyticsService);

	constructor() {
		effect(() => {
			const forms = this.formOptions_();
			const currentSelectedForm = this.selectedFormId_();

			if (forms.length > 0 && !currentSelectedForm) {
				this.selectedFormId_.set(forms[0].formId);
			}
		});

		effect(() => {
			const tab = this.currentTab_();

			if (tab === 'added-to-matter') {
				this.analyticsService.logEvent(
					AnalyticsEvent.INTAKE_PARTIES_REVIEW_RESPONSES_ADDED,
				);
				return;
			}

			if (tab === 'missing-responses') {
				this.analyticsService.logEvent(
					AnalyticsEvent.INTAKE_PARTIES_REVIEW_RESPONSES_MISSING,
				);
				return;
			}
		});
	}

	queryParams_ = toSignal(this.route.queryParams, { initialValue: {} });
	currentTab_ = computed(() => this.queryParams_()?.['tab'] || 'all');

	formOptions_ = computed(() => this.viewModelData_()?.formOptions || []);

	currentFormData_ = computed(() => {
		const data = this.viewModelData_();
		const formId = this.selectedFormId_();
		if (!data || !formId || !data.formData[formId]) return null;
		return data.formData[formId];
	});

	statusCounts_ = computed(() => {
		const formData = this.currentFormData_();
		return (
			formData?.counts || {
				all: 0,
				'ready-to-review': 0,
				'added-to-matter': 0,
				'missing-responses': 0,
				custom: 0,
			}
		);
	});

	allSections_ = computed(() => {
		const formData = this.currentFormData_();
		return this.applySectionsWithCheckboxStates(
			formData?.sectionsByStatus['all'] || [],
		);
	});

	customQuestionsSections_ = computed(() => {
		const formData = this.currentFormData_();
		return this.applySectionsWithCheckboxStates(
			formData?.sectionsByStatus[FieldResponseStatus.CUSTOM] || [],
		);
	});

	readyToReviewSections_ = computed(() => {
		const formData = this.currentFormData_();
		return this.applySectionsWithCheckboxStates(
			formData?.sectionsByStatus[FieldResponseStatus.READY_TO_REVIEW] || [],
		);
	});

	addedToMatterSections_ = computed(() => {
		const formData = this.currentFormData_();
		return this.applySectionsWithCheckboxStates(
			formData?.sectionsByStatus[FieldResponseStatus.ADDED_TO_MATTER] || [],
		);
	});

	missingResponsesSections_ = computed(() => {
		const formData = this.currentFormData_();
		return this.applySectionsWithCheckboxStates(
			formData?.sectionsByStatus[FieldResponseStatus.MISSING_RESPONSES] || [],
		);
	});

	allCount_ = computed(() => this.statusCounts_().all);
	readyToReviewCount_ = computed(
		() => this.statusCounts_()[FieldResponseStatus.READY_TO_REVIEW],
	);
	addedToMatterCount_ = computed(
		() => this.statusCounts_()[FieldResponseStatus.ADDED_TO_MATTER],
	);
	missingResponsesCount_ = computed(
		() => this.statusCounts_()[FieldResponseStatus.MISSING_RESPONSES],
	);
	customQuestionsCount_ = computed(
		() => this.statusCounts_()[FieldResponseStatus.CUSTOM],
	);

	kebabItems = [
		{
			id: KebabOption.PreviewForm,
			title: {
				label: 'Preview form',
				icon: 'eye',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navigationService.goToMatter_ClientIntake_PreviewMode(
					this.viewModelData_().matterId,
				);
			},
		},
		{
			id: KebabOption.EditForm,
			title: {
				label: 'Edit form',
				icon: 'edit',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.navigationService.goToMatter_ClientIntake_EditMode(
					this.viewModelData_().matterId,
				);
			},
		},
		{
			id: KebabOption.MarkAsComplete,
			title: {
				label: 'Mark as complete',
				icon: 'check',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(IntakeFormStatus.COMPLETE);
			},
		},
		{
			id: KebabOption.Cancel,
			title: {
				label: 'Cancel',
				icon: 'circle-x',
				iconClasses: 'text-gray-500',
			},
			action: () => {
				this.clientIntakeService.setIntakeFormStatus(
					IntakeFormStatus.CANCELLED,
				);
			},
		},
	];

	private applySectionsWithCheckboxStates(
		sections: IntakeResponseSection[],
	): IntakeResponseSection[] {
		const formId = this.selectedFormId_();
		if (!formId) return sections;

		const checkboxStatesValue = this.checkboxStates_();
		const formCheckboxStates = checkboxStatesValue[formId] || {};

		return sections.map((section) => ({
			...section,
			data: section.data.map((sectionItem) => ({
				...sectionItem,
				isSelected: formCheckboxStates[sectionItem.questionId] || false,
			})),
		}));
	}

	onFormSelected(formId: string): void {
		this.selectedFormId_.set(formId);
	}

	onItemToggled(item: ReviewResponseItem, selected: boolean): void {
		if (item.clientId) {
			const newStates = toggleItemSelection(
				this.checkboxStates_(),
				item.clientId,
				item.questionId,
				selected,
			);
			this.checkboxStates_.set(newStates);
		}
	}

	onRowToggled(event: { row: IntakeResponseRow; checked: boolean }): void {
		const currentFormId = this.selectedFormId_();
		if (currentFormId) {
			const newStates = toggleItemSelection(
				this.checkboxStates_(),
				currentFormId,
				event.row.questionId,
				event.checked,
			);
			this.checkboxStates_.set(newStates);
		}
	}

	onSelectAll(selected: boolean): void {
		const currentFormId = this.selectedFormId_();
		const formData = this.currentFormData_();
		const currentTab = this.currentTab_();

		if (currentFormId && formData && formData.itemsByStatus[currentTab]) {
			const items = formData.itemsByStatus[currentTab];

			if (items.length === 0) {
				return;
			}

			const newStates = selectAllForForm(
				this.checkboxStates_(),
				currentFormId,
				items,
				selected,
			);
			this.checkboxStates_.set(newStates);
		}
	}

	getFilteredItems(): ReviewResponseItem[] {
		const formData = this.currentFormData_();
		const currentTab = this.currentTab_();
		if (!formData) return [];

		const items = formData.itemsByStatus[currentTab] || [];
		const formId = this.selectedFormId_();
		if (!formId) return items;

		return applyCheckboxStates(items, this.checkboxStates_(), formId);
	}

	areAllItemsSelected(): boolean {
		const selectedCount = this.getSelectedItemsCount();

		if (selectedCount === 0) return false;

		const currentTab = this.currentTab_();
		const formData = this.currentFormData_();

		if (!formData) return false;

		const currentTabItems = formData.itemsByStatus[currentTab] || [];

		const totalSelectableItems: number = currentTabItems.filter(
			(item) => item.status === FieldResponseStatus.READY_TO_REVIEW,
		).length;

		return selectedCount === totalSelectableItems;
	}

	getSelectedItemsCount(): number {
		const formData = this.currentFormData_();
		const formId = this.selectedFormId_();
		const currentTab = this.currentTab_();
		if (!formData || !formId) return 0;

		const checkboxStatesValue = this.checkboxStates_();
		const formCheckboxStates = checkboxStatesValue[formId] || {};

		const currentTabItems = formData.itemsByStatus[currentTab] || [];
		return currentTabItems.filter((item) => {
			if (typeof item.clientResponse !== 'string') {
				return formCheckboxStates[item.questionId] && item.clientResponse;
			}

			return (
				formCheckboxStates[item.questionId] &&
				item.clientResponse &&
				(typeof item.clientResponse !== 'string' ||
					item.clientResponse.trim() !== '')
			);
		}).length;
	}

	submitResponsesToMatter = async () => {
		const businessId = this.businessId();
		const matterId = this.matterId();
		const currentFormId = this.selectedFormId_();
		const currentTab = this.currentTab_();
		const formData = this.currentFormData_();

		if (!formData || !currentFormId) return;

		const checkboxStatesValue = this.checkboxStates_();
		const formCheckboxStates = checkboxStatesValue[currentFormId] || {};

		const currentTabItems: ReviewResponseItem[] =
			formData.itemsByStatus[currentTab] || [];

		const selectedItems = currentTabItems.filter((item) => {
			if (typeof item.clientResponse !== 'string') {
				return formCheckboxStates[item.questionId] && item.clientResponse;
			}

			return (
				formCheckboxStates[item.questionId] &&
				item.clientResponse &&
				(typeof item.clientResponse !== 'string' ||
					item.clientResponse.trim() !== '')
			);
		});

		const selectedItemsForSubmission: FormAnswerRequest[] = selectedItems.map(
			(answer) => {
				if (
					answer.clientResponse &&
					typeof answer.clientResponse === 'object' &&
					'fileName' in answer.clientResponse
				) {
					return {
						questionId: answer.questionId,
						answer: (answer.clientResponse as FileUploadMetaData).fileName,
						mapTo: answer.mapTo,
						uploadOnlyConfig: answer.uploadOnlyConfig,
						answerAsFileUpload: answer.clientResponse,
					};
				}

				return {
					questionId: answer.questionId,
					answer: answer.clientResponse as string,
					mapTo: answer.mapTo,
					uploadOnlyConfig: answer.uploadOnlyConfig,
				};
			},
		);

		const intakeFormId = this.viewModelData_()?.intakeFormId;
		if (!businessId || !matterId || !intakeFormId || !currentFormId) {
			console.error('Missing required data for submission');
			return;
		}

		if (selectedItemsForSubmission.length === 0) {
			console.error('No items selected for submission');
			return;
		}

		const currentResponse = this.currentClientIntakeResponse().find(
			(response) => response.id === currentFormId,
		);

		if (!currentResponse) {
			console.error('Could not find response document for current form');
			return;
		}

		const formAnswers = selectedItemsForSubmission;

		const requestBody: SubmitIntakeFormResponsesRequest = {
			intakeFormId: intakeFormId,
			businessId: businessId,
			matterId: matterId,
			clientId: currentResponse.clientId,
			formId: currentResponse.id,
			formAnswers: formAnswers,
		};

		Logger.info('Request body', requestBody);

		try {
			await firstValueFrom(
				this.httpClient.post<SubmitIntakeFormResponsesResponse>(
					`${environment.api.baseUrl}/client-portal/map-responses-to-matter`,
					requestBody,
				),
			);

			console.log('Form responses submitted successfully');
		} catch (error) {
			console.error('Error submitting form responses:', error);
		}
	};

	goBack() {
		if (this.fileType() === MatterFileType.REFINANCE) {
			this.navigationService.goToMatter_Parties_Mortgagors(this.matterId());
			return;
		}

		this.navigationService.goToMatter_Parties(this.matterId());
	}
}
